import { createContext, useCallback, useContext, useMemo, useState } from "react"

/** Prebuilt's Video quality choices. */
export type VideoQualityPreset = "auto" | "high" | "low" | "bandwidth-saver"
/** Prebuilt's Chat notifications choices. */
export type ChatNotificationsPreference = "when-closed" | "always" | "never"
/** Emoji skin tone modifiers, "" for the default yellow. */
export type EmojiSkinTone = "" | "🏻" | "🏼" | "🏽" | "🏾" | "🏿"
/** Google Meet's automatic picture-in-picture choices. */
export type AutoPictureInPicturePreference = "always" | "tab-switch" | "screen-share" | "manual"

export const AUTO_PICTURE_IN_PICTURE_OPTIONS: Array<{ id: AutoPictureInPicturePreference; label: string }> = [
  { id: "always", label: "Always automatically show" },
  { id: "tab-switch", label: "Only when I switch tabs" },
  { id: "screen-share", label: "Only when I screen share (window & entire screen)" },
  { id: "manual", label: "Never automatically show" },
]

export type CallPreferences = {
  videoQuality: VideoQualityPreset
  noiseReduction: boolean
  chatNotifications: ChatNotificationsPreference
  emojiSkinTone: EmojiSkinTone
  autoPictureInPicture: AutoPictureInPicturePreference
}

export const DEFAULT_CALL_PREFERENCES: CallPreferences = {
  videoQuality: "auto",
  noiseReduction: false,
  chatNotifications: "when-closed",
  emojiSkinTone: "",
  autoPictureInPicture: "always",
}

export const CALL_PREFERENCES_STORAGE_KEY = "wisefriend.call.preferences"

const VIDEO_QUALITY_VALUES: VideoQualityPreset[] = ["auto", "high", "low", "bandwidth-saver"]
const CHAT_NOTIFICATION_VALUES: ChatNotificationsPreference[] = ["when-closed", "always", "never"]
export const EMOJI_SKIN_TONES: EmojiSkinTone[] = ["", "🏻", "🏼", "🏽", "🏾", "🏿"]
const AUTO_PIP_VALUES: AutoPictureInPicturePreference[] = AUTO_PICTURE_IN_PICTURE_OPTIONS.map((option) => option.id)

export const parseCallPreferences = (raw: unknown): CallPreferences => {
  const source = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {}
  const videoQuality = VIDEO_QUALITY_VALUES.includes(source.videoQuality as VideoQualityPreset) ? (source.videoQuality as VideoQualityPreset) : DEFAULT_CALL_PREFERENCES.videoQuality
  const chatNotifications = CHAT_NOTIFICATION_VALUES.includes(source.chatNotifications as ChatNotificationsPreference)
    ? (source.chatNotifications as ChatNotificationsPreference)
    : DEFAULT_CALL_PREFERENCES.chatNotifications
  const emojiSkinTone = EMOJI_SKIN_TONES.includes(source.emojiSkinTone as EmojiSkinTone) ? (source.emojiSkinTone as EmojiSkinTone) : DEFAULT_CALL_PREFERENCES.emojiSkinTone
  const autoPictureInPicture = AUTO_PIP_VALUES.includes(source.autoPictureInPicture as AutoPictureInPicturePreference)
    ? (source.autoPictureInPicture as AutoPictureInPicturePreference)
    : DEFAULT_CALL_PREFERENCES.autoPictureInPicture
  return {
    videoQuality,
    noiseReduction: typeof source.noiseReduction === "boolean" ? source.noiseReduction : DEFAULT_CALL_PREFERENCES.noiseReduction,
    chatNotifications,
    emojiSkinTone,
    autoPictureInPicture,
  }
}

export const readStoredCallPreferences = (): CallPreferences => {
  try {
    const raw = window.localStorage.getItem(CALL_PREFERENCES_STORAGE_KEY)
    return parseCallPreferences(raw ? JSON.parse(raw) : null)
  } catch {
    return { ...DEFAULT_CALL_PREFERENCES }
  }
}

const storeCallPreferences = (preferences: CallPreferences) => {
  try {
    window.localStorage.setItem(CALL_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences))
  } catch {
    // Private mode or blocked storage: preferences last for this page only.
  }
}

export type CallPreferencesState = {
  preferences: CallPreferences
  updatePreferences: (patch: Partial<CallPreferences>) => void
}

const CallPreferencesContext = createContext<CallPreferencesState>({
  preferences: DEFAULT_CALL_PREFERENCES,
  updatePreferences: () => {},
})
export const CallPreferencesProvider = CallPreferencesContext.Provider
export const useCallPreferencesContext = () => useContext(CallPreferencesContext)

/**
 * The per-browser preferences behind Prebuilt's Settings dialog (video quality, noise
 * reduction, chat notifications, emoji skin tone), persisted in local storage.
 */
export function useCallPreferences(): CallPreferencesState {
  const [preferences, setPreferences] = useState<CallPreferences>(() => readStoredCallPreferences())
  const updatePreferences = useCallback((patch: Partial<CallPreferences>) => {
    setPreferences((current) => {
      const next = parseCallPreferences({ ...current, ...patch })
      storeCallPreferences(next)
      return next
    })
  }, [])
  return useMemo(() => ({ preferences, updatePreferences }), [preferences, updatePreferences])
}
